// Funding (QOR staff, docs/PARTNER_API.md § 4.5), in three tabs (?tab=…, client's ask 2026-10-31):
// - Partner funds (the ABL ledger): every partner's fund, its history, what partners owe QOR and collecting it, and
//   the approvals of partner fund changes;
// - Central Payments MFA: what QOR holds at the processor against what the MFA itself shows, every balance read, and
//   the money QOR wired there (asked, approved, booked);
// - Risk & settings: cash flow, and the platform-wide funding settings.
// Alerts stay above the tabs: a critical one is never hidden behind one. Every fund change waits for a second system
// administrator.
import { shortDate } from '@qor/web-shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fundsApi, type Approval } from '../api';
import { approveQuestion } from '../components/Approvals';
import { usePageTitle } from '../components/ConsoleLayout';
import { useStaff } from '../components/RequireStaff';
import { confirmed, PageHeading, ResultBanner } from '../components/ui';
import { FundingPartnersTab } from './FundingPartnersTab';
import { FundingProcessorTab } from './FundingProcessorTab';
import { FundingRiskTab } from './FundingRiskTab';

const TABS = [
  ['partners', 'Partner funds'],
  ['mfa', 'Central Payments MFA'],
  ['risk', 'Risk & settings'],
] as const;
type Tab = (typeof TABS)[number][0];

/** What a tab needs from the page: who is looking, the busy flag, and how to say what happened. */
export interface FundingTabProps {
  editable: boolean;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  done: (ok: boolean, message: string) => Promise<void>;
  decide: (approval: Approval, approve: boolean) => Promise<void>;
}

export function FundingPage() {
  usePageTitle('Funding');
  const client = useQueryClient();
  const { user } = useStaff();
  const editable = user.role === 'SystemAdmin';
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<{ message: string | null; ok: boolean }>({ message: null, ok: false });
  const health = useQuery({ queryKey: ['funds-health'], queryFn: () => fundsApi.health() });
  const alertLog = useQuery({ queryKey: ['funds-alerts'], queryFn: () => fundsApi.alertLog() });
  const pending = {
    partner: useQuery({
      queryKey: ['approvals', 'pending', 'partner'],
      queryFn: () => fundsApi.approvals('pending', 1, 'partner'),
    }),
    processor: useQuery({
      queryKey: ['approvals', 'pending', 'processor'],
      queryFn: () => fundsApi.approvals('pending', 1, 'processor'),
    }),
    other: useQuery({
      queryKey: ['approvals', 'pending', 'other'],
      queryFn: () => fundsApi.approvals('pending', 1, 'other'),
    }),
  };
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.find(([key]) => key === params.get('tab'))?.[0] ?? 'partners';

  async function done(ok: boolean, message: string) {
    setBusy(false);
    setOutcome({ ok, message });
    await Promise.all(
      [
        'funds-health',
        'funds-alerts',
        'receivables',
        'approvals',
        'funds-settings',
        'funds',
        'partner-entries',
        'processor-entries',
        'processor-snapshots',
        'opencp-account',
      ].map((k) => client.invalidateQueries({ queryKey: [k] })),
    );
  }

  async function decide(a: Approval, approve: boolean) {
    if (approve && !confirmed(approveQuestion(a))) return;
    const note = approve ? null : window.prompt('Why (optional)?');
    if (!approve && note === null) return;
    setBusy(true);
    const r = approve ? await fundsApi.approve(a.id) : await fundsApi.reject(a.id, note?.trim() || null);
    await done(r.ok, r.ok ? (approve ? 'Approved and done.' : 'Turned down.') : r.error);
  }

  async function acknowledge(key: string) {
    setBusy(true);
    const r = await fundsApi.acknowledgeAlert(key);
    await done(r.ok, r.ok ? 'Acknowledged: it will not be e-mailed again while it lasts.' : r.error);
  }
  async function sendReport() {
    setBusy(true);
    const r = await fundsApi.sendReport();
    await done(r.ok, r.ok ? 'The daily risk report was e-mailed to QOR’s system administrators.' : r.error);
  }
  async function runChecks() {
    setBusy(true);
    const r = await fundsApi.runChecks();
    await done(
      r.ok,
      r.ok
        ? `Checked: ${r.data.raised} new alert(s), ${r.data.repeated} critical one(s) still unacknowledged, ${r.data.resolved} cleared${r.data.frozen.length ? `, ${r.data.frozen.length} partner(s) frozen for an overdue advance` : ''}. Staff were e-mailed.`
        : r.error,
    );
  }
  async function freezeAll() {
    if (!confirmed('Freeze every new load of every prefunded partner? Lifting it needs a second system administrator.'))
      return;
    setBusy(true);
    const r = await fundsApi.freeze(null);
    await done(r.ok, r.ok ? 'Loads frozen for every partner.' : r.error);
  }
  async function resumeAll() {
    setBusy(true);
    const r = await fundsApi.resume(null);
    await done(r.ok, r.ok ? 'Asked to resume loads: a second system administrator must approve it.' : r.error);
  }

  const h = health.data;
  const waiting = {
    partners: pending.partner.data?.total ?? 0,
    mfa: pending.processor.data?.total ?? 0,
    risk: pending.other.data?.total ?? 0,
  };
  const props: FundingTabProps = { editable, busy, setBusy, done, decide };

  return (
    <>
      <PageHeading
        title="Funding"
        actions={
          editable && (
            <>
              <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void runChecks()}>
                Run checks now
              </button>{' '}
              <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void sendReport()}>
                E-mail the risk report
              </button>{' '}
              {h?.frozen ? (
                <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void resumeAll()}>
                  Ask to resume all loads
                </button>
              ) : (
                <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void freezeAll()}>
                  Freeze all loads
                </button>
              )}
            </>
          )
        }
      />
      <p className="qc-breadcrumb" style={{ marginBottom: 12 }}>
        Partners&apos; prefunded money, and what QOR really holds at each processor. Checked every hour; new warnings
        and critical alerts are e-mailed to QOR&apos;s system administrators.
      </p>
      <ResultBanner {...outcome} />

      {h && (
        <div className="qc-panel" style={{ marginBottom: 16 }}>
          <div className="qc-panel-title">
            <h3>Alerts</h3>
            <span>As of {shortDate(h.checkedAt)}</span>
          </div>
          {h.alerts.length === 0 ? (
            <p className="qc-empty">All clear.</p>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {h.alerts.map((a) => (
                <li key={a.key} style={{ marginBottom: 6 }}>
                  <span className={`qc-badge ${a.level === 'critical' ? 'qc-badge-danger' : 'qc-badge-warning'}`}>
                    {a.level}
                  </span>{' '}
                  {a.message} {a.programId && <Link to={`/partners/${a.programId}?tab=funding`}>Open partner</Link>}
                  {a.key === 'unknown_loads' && <Link to="/loads?status=unknown">Open loads</Link>}{' '}
                  {(() => {
                    const record = alertLog.data?.find((l) => l.key === a.key && !l.resolvedAt);
                    if (record?.acknowledgedAt)
                      return (
                        <small>
                          · acknowledged by {record.acknowledgedBy}, {shortDate(record.acknowledgedAt)}
                        </small>
                      );
                    return record && a.level === 'critical' ? (
                      <button
                        type="button"
                        className="qc-button-ghost"
                        disabled={busy}
                        onClick={() => void acknowledge(a.key)}
                      >
                        Acknowledge
                      </button>
                    ) : null;
                  })()}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="qc-segmented" role="tablist" aria-label="Funding sections" style={{ marginBottom: 16 }}>
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-pressed={tab === key}
            aria-selected={tab === key}
            onClick={() => setParams(key === 'partners' ? {} : { tab: key }, { replace: true })}
          >
            {label}
            {waiting[key] > 0 && (
              <>
                {' '}
                <span className="qc-badge qc-badge-warning" aria-label={`${waiting[key]} waiting for approval`}>
                  {waiting[key]}
                </span>
              </>
            )}
          </button>
        ))}
      </div>

      {tab === 'partners' ? (
        <FundingPartnersTab {...props} health={h ?? null} pending={pending.partner.data?.items ?? null} />
      ) : tab === 'mfa' ? (
        <FundingProcessorTab {...props} health={h ?? null} pending={pending.processor.data?.items ?? null} />
      ) : (
        <FundingRiskTab {...props} health={h ?? null} />
      )}
    </>
  );
}
